import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetVehicleTurnaroundTimeResponse } from "subModule/src/services/analytics/interface";
import { useGetVehicleTurnaroundService } from "subModule/src/services/analytics/useAnalytics";
import { PieChart } from "@mui/x-charts/PieChart";
import {
  useGetAccountsDropdownService,
  useGetClientsDropdownService,
  useGetDriversService,
} from "subModule/src/services/client/useClient";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleSx,
  dashboardWidgetIconSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

interface VehicleTurnaroundTimeProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleStationActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

const compactSelectSx = {
  "& .css-7896aj-control, & .css-1xiok1u-control": {
    minHeight: "auto",
    fontSize: '9px',
    height: "auto",
    overflow: "hidden",
    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
      {
        background: "#fff",
        padding: 0,
        width: "15px",
        height: "15px",
      },
    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator": {
      position: "absolute",
      right: 0,
    },
    border: 0,
  },
};

const WITHIN_24 = "#22C55E";
const ABOVE_48 = "#EF4444";

export default function VehicleTurnaroundTime(
  props: VehicleTurnaroundTimeProps,
) {
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<any>(null);
  const screenWidth = useMediaQuery("(max-width: 680px)");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [page, setPage] = useState(1);
  const [dropdownLoading, setDropdownLoading] = useState<string | null>(null);
  const debouncingTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      allStation: false,
    },
  });
  const values = watch(["stationId", "customerId", "accountId", "allStation"]);
  const primary = props.themes?.primary || "#3B82F6";
  const secondary = props.themes?.secondary || "#F97316";
  const filtersOnNextLine =
    screenWidth || Boolean(watch("customerType")?.value);

  const { refetch: apiRefetch } = useGetVehicleTurnaroundService({
    stationId: values[0]?.value || null,
    range: "monthly",
    customerId: values[1]?.value,
    accountId: values[2]?.value,
    allStation: values[3],
  });
  const { data: clientsData, refetch: clientsRefetch } = watch("accountId")
    ?.value
    ? useGetDriversService(
        {
          pageNumber: page,
          size: 50,
          active: true,
          type: [160, 180],
          name: searchTerm,
        },
        watch("accountId")?.value,
      )
    : useGetClientsDropdownService({
        pageNumber: page,
        size: 50,
        type: [170],
        search: searchTerm,
      });
  const { data: accountsData, refetch: accountsRefetch } =
    useGetAccountsDropdownService({
      pageNumber: page,
      size: 50,
      search: searchTerm,
    });
  const clients = (clientsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; name?: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  const accounts = (accountsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string | null }) => ({
      value: item.id,
      label: item?.title || item.id,
    }),
  );

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);

  useEffect(() => {
    if (values[1]?.value || values[2]?.value) {
      setTimeout(() => fetchData(), 50);
    }
  }, [values[1]?.value, values[2]?.value]);

  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetVehicleTurnaroundTimeResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => console.error("===", err))
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPage(1);
      setSearchTerm(search);
      if (search) setDropdownLoading(typeName);
    } else {
      setPage(page + 1);
      setDropdownLoading(typeName);
    }
    if (debouncingTimer.current) clearTimeout(debouncingTimer.current);
    debouncingTimer.current = setTimeout(() => {
      if (typeName === "accounts")
        accountsRefetch().finally(() => setDropdownLoading(null));
      else if (typeName === "customers")
        clientsRefetch().finally(() => setDropdownLoading(null));
    }, 300);
  };

  const period = data?.monthly || {};
  const within24 = Number(period.within24h || 0);
  const within48 = Number(period.within48h || 0);
  const above48 = Number(period.above48h || 0);
  const allZero = within24 + within48 + above48 === 0;

  const segments = [
    {
      key: "within24h",
      labelKey: "within24h",
      value: within24,
      color: WITHIN_24,
    },
    {
      key: "within48h",
      labelKey: "within48h",
      value: within48,
      color: primary,
    },
    {
      key: "above48h",
      labelKey: "above48h",
      value: above48,
      color: ABOVE_48,
    },
  ];

  const dominant = segments.reduce(
    (max, item) => (item.value > max.value ? item : max),
    segments[0],
  );

  const chartData = segments.map((item, index) => ({
    id: index,
    label: t(item.labelKey),
    value: allZero ? 1 : Math.abs(item.value),
    color: item.color,
  }));

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 270,
        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
        borderRadius: "12px",
        border: "1px solid #EEF0F3",
        "& .MuiCardContent-root": {
          padding: "16px !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <CardContent>
        <Box
          sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0, mb: 1.5 }}
        >
          <Box sx={dashboardWidgetTitleRowSx}>
            <Box
              sx={{
                ...dashboardWidgetIconSx,
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: `${primary}1F`,
                color: primary,
                
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { fontSize: 17, color: primary },
              }}
            >
              <AccessTimeOutlinedIcon />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "2px",
              }}
            >
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: { xs: 12, sm: 15 },
              fontWeight: { xs: 600, sm: 700 },
              textAlign: "left",
                  color: "#0F2744",
                  lineHeight: 1.25,
                }}
              >
                {t("vehicleTurnaroundTime")}
              </Typography>
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#9CA3AF",
                  lineHeight: 1.3,
                }}
              >
                {t("timeAnalysisForVehicleTurnaround")}
              </Typography>
            </Box>
          </Box>

          <Box
            className="flex flex-wrap"
            sx={{
              flexShrink: 0,
              gap: 0.5,
              ...dashboardWidgetFiltersSx,
            }}
          >
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "vehicleTurnaround"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleStationActions(
                          "vehicleTurnaround",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleStationActions(
                            "vehicleTurnaround",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleStationActions(
                          "vehicleTurnaround",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="customerType"
              control={control}
              defaultValue={null}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={[
                      { value: "Individual", label: t("Individual") },
                      { value: "company", label: t("company") },
                    ]}
                    isSearchable
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setValue("accountId", null);
                      setValue("customerId", null);
                      if (watch("customerType")?.value === "Individual")
                        clientsRefetch();
                      else if (watch("customerType")?.value === "company")
                        accountsRefetch();
                    }}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("select") + " " + t("type")}
                    value={
                      field.value
                        ? {
                            value: field.value.value,
                            label: t(
                              field.value.value === "Individual"
                                ? "Individual"
                                : "company",
                            ),
                          }
                        : null
                    }
                  />
                </Box>
              )}
            />
            {watch("customerType")?.value === "company" ? (
              <Controller
                name="accountId"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <Box sx={compactSelectSx}>
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={accounts}
                      isSearchable
                      isDisabled={loading}
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setValue("customerId", null);
                        setTimeout(() => clientsRefetch(), 50);
                      }}
                      error={errors.accountId?.message as string}
                      ddWidth="30px"
                      ddHeight="30px"
                      defaultPlaceholder={t("select") + " " + t("company")}
                      isLoading={dropdownLoading === "accounts"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions("accounts", "searchIt", search);
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (searchTerm || page > 1)
                            handleDropdownActions("accounts", "searchIt", "");
                          else setSearchTerm("");
                          setPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(accountsData?.pages[0]?.data as any).last)
                          handleDropdownActions("accounts", "loadNext");
                      }}
                      page={page}
                    />
                  </Box>
                )}
              />
            ) : null}
            {watch("customerType")?.value === "Individual" ||
            watch("accountId")?.value ? (
              <Controller
                name="customerId"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <Box sx={compactSelectSx}>
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={clients}
                      isSearchable
                      isDisabled={loading}
                      onChange={(selectedOption: any) =>
                        field.onChange(selectedOption)
                      }
                      error={errors.customerId?.message as string}
                      ddWidth="30px"
                      ddHeight="30px"
                      defaultPlaceholder={
                        t("select") +
                        " " +
                        (watch("accountId")?.value
                          ? t("driver")
                          : t("customer"))
                      }
                      isLoading={dropdownLoading === "customers"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          handleDropdownActions(
                            "customers",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (searchTerm || page > 1)
                            handleDropdownActions("customers", "searchIt", "");
                          else setSearchTerm("");
                          setPage(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(clientsData?.pages[0]?.data as any).last)
                          handleDropdownActions("customers", "loadNext");
                      }}
                      page={page}
                    />
                  </Box>
                )}
              />
            ) : null}
          </Box>
        </Box>

        {loading ? (
          <Box
            className="flex flex-col sm:flex-row items-center gap-3"
            sx={{ flex: 1, width: "100%" }}
          >
            <Skeleton
              variant="circular"
              width={120}
              height={120}
              sx={{ backgroundColor: secondary, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, width: "100%" }}>
              <Skeleton sx={{ backgroundColor: secondary, mb: 1 }} />
              <Skeleton sx={{ backgroundColor: secondary, mb: 1 }} />
              <Skeleton sx={{ backgroundColor: secondary }} />
            </Box>
          </Box>
        ) : (
          <Box
            className="flex flex-col sm:flex-row items-center"
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              gap: { xs: 2, sm: 3 },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 120,
                height: 120,
                flexShrink: 0,
              }}
            >
              <PieChart
                height={120}
                width={120}
                series={[
                  {
                    data: chartData,
                    valueFormatter: (v) =>
                      `${allZero ? 0 : v.value?.toLocaleString() || 0}`,
                    innerRadius: 40,
                    outerRadius: 54,
                    cx: 60,
                    cy: 60,
                    paddingAngle: 2,
                    cornerRadius: 4,
                  },
                ]}
                slotProps={{ legend: { hidden: true } }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 72,
                  height: 72,
                  maxWidth: 72,
                  maxHeight: 72,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                  textAlign: "center",
                  overflow: "hidden",
                  px: 0.5,
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize:
                      String(dominant.value).replace(".", "").length >= 4
                        ? 13
                        : 16,
                    fontWeight: 800,
                    color: allZero ? "#0F2744" : dominant.color,
                    lineHeight: 1.05,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {allZero
                    ? "0%"
                    : `${Number(dominant.value).toLocaleString("en", {
                        maximumFractionDigits: 1,
                      })}%`}
                </Typography>
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 8,
                    fontWeight: 600,
                    color: "#6B7280",
                    mt: 0.25,
                    maxWidth: "100%",
                    lineHeight: 1.15,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                  }}
                >
                  {t(dominant.labelKey)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                width: "100%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                justifyContent: "center",
              }}
            >
              {segments.map((item) => (
                <Tooltip key={item.key} title={`${item.value}%`} enterDelay={0}>
                  <Box
                    className="flex items-center justify-between gap-2"
                    sx={{ width: "100%" }}
                  >
                    <Box sx={dashboardWidgetTitleRowSx}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: 12,
                          color: "#6B7280",
                          fontWeight: 500,
                          lineHeight: 1.2,
                        }}
                      >
                        {t(item.labelKey)}
                      </Typography>
                    </Box>
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 15,
                        fontWeight: 800,
                        color: item.color,
                        lineHeight: 1.2,
                        flexShrink: 0,
                      }}
                    >
                      {item.value}%
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
